/**
 * PixPaymentPanel — Painel premium de pagamento via PIX.
 *
 * Layout: QR Code grande à esquerda, painel informativo + copia-e-cola à direita.
 *
 * RELEASE: 2026-06-21 v2.0.0 — Redesign premium (QR à esquerda, painel à direita,
 *                              selo PIX, mensagem comercial usando e-mail do pagador).
 * RELEASE: 2026-06-20 v1.1.0 — Envelope padronizado de erro.
 * RELEASE: 2026-06-20 v1.0.0 — Sub-fase B (PIX).
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';
import { CheckoutErrorBanner } from './components/CheckoutErrorBanner';
import { getFallbackMessage, getNetworkErrorMessage } from '@/lib/checkout/userMessages';

const logger = createLogger('PixPaymentPanel');

export interface PixPaymentPanelProps {
  slug: string;
  /** E-mail do pagador (do payload customer_full) — usado na mensagem de confirmação. */
  email?: string | null;
  onPaid?: () => void;
}

interface PixData {
  qr_code_image: string | null;
  copy_paste: string | null;
  expires_at: string | null;
}

export function PixPaymentPanel({ slug, email, onPaid }: PixPaymentPanelProps) {
  const [pix, setPix] = useState<PixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState<
    { message: string; code?: string | null; supportId?: string | null } | null
  >(null);
  const [copied, setCopied] = useState(false);
  const [paid, setPaid] = useState(false);

  const loadPix = async () => {
    setErrorState(null);
    setLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke(
        'public-checkout',
        { body: { action: 'get_pix', slug } },
      );
      if (invokeErr) {
        logger.warn('Falha de rede no get_pix', { err: invokeErr.message });
        setErrorState({ message: getNetworkErrorMessage(), code: 'NETWORK_ERROR' });
        return;
      }
      const payload = (data ?? {}) as PixData & {
        success?: boolean;
        error_code?: string;
        user_message?: string;
        support_id?: string;
      };
      if (payload?.success === false || payload?.error_code) {
        setErrorState({
          message: payload.user_message || getFallbackMessage(payload.error_code),
          code: payload.error_code ?? null,
          supportId: payload.support_id ?? null,
        });
        return;
      }
      setPix(payload);
    } catch (err) {
      logger.error('Falha ao carregar PIX', { err: String(err) });
      setErrorState({ message: getNetworkErrorMessage(), code: 'NETWORK_ERROR' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!pix || paid) return;
    let cancelled = false;
    let delay = 3000;
    const start = Date.now();
    const MAX_MS = 10 * 60 * 1000;

    const tick = async () => {
      if (cancelled || Date.now() - start > MAX_MS) return;
      try {
        const { data, error: pErr } = await supabase.functions.invoke(
          'public-checkout',
          { body: { action: 'get_status', slug } },
        );
        if (!pErr) {
          const status = (data as { status?: string })?.status;
          if (status === 'pago') {
            setPaid(true);
            onPaid?.();
            return;
          }
        }
      } catch (err) {
        logger.warn('Polling exception', { err: String(err) });
      }
      delay = Math.min(delay * 1.2, 8000);
      if (!cancelled) setTimeout(tick, delay);
    };
    const id = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [pix, paid, slug, onPaid]);

  const handleCopy = async () => {
    if (!pix?.copy_paste) return;
    try {
      await navigator.clipboard.writeText(pix.copy_paste);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      logger.warn('Clipboard write failed', { err: String(err) });
    }
  };

  if (paid) {
    return (
      <div className="text-center py-12 space-y-3">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h2 className="text-xl font-semibold">Pagamento confirmado</h2>
        <p className="text-sm text-muted-foreground">
          Recebemos o seu PIX. Você receberá o comprovante por e-mail.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (errorState) {
    return (
      <div className="space-y-3 p-4">
        <CheckoutErrorBanner
          message={errorState.message}
          code={errorState.code ?? undefined}
          supportId={errorState.supportId ?? undefined}
        />
        <Button size="sm" variant="outline" onClick={loadPix}>
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Tentar novamente
        </Button>
      </div>
    );
  }

  // Trunca o copia-e-cola para exibição compacta.
  const copyPaste = pix?.copy_paste ?? '';
  const truncatedCopy =
    copyPaste.length > 48 ? `${copyPaste.slice(0, 48)}…` : copyPaste;

  return (
    <div className="space-y-6">
      {/* Cabeçalho do método — combina com o padrão "Modern institutional" */}
      <header className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Pague em segundos
          </p>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <PixGlyph className="text-[var(--brand-primary)]" /> Pagamento via PIX
          </h3>
        </div>
        <span
          className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Aprovação imediata
        </span>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-start">
        {/* QR Code com moldura sutil de marca */}
        <div className="mx-auto md:mx-0">
          {pix?.qr_code_image ? (
            <div className="relative p-1 rounded-[1.25rem] bg-gradient-to-br from-slate-200/70 via-slate-100 to-[color-mix(in_srgb,var(--brand-primary)_15%,white)] shadow-sm">
              <div className="p-3 md:p-4 bg-white rounded-2xl">
                <img
                  src={`data:image/png;base64,${pix.qr_code_image}`}
                  alt="QR Code PIX"
                  className="w-56 h-56 md:w-64 md:h-64 object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="w-56 h-56 md:w-64 md:h-64 rounded-2xl bg-slate-100 flex items-center justify-center text-sm text-muted-foreground">
              QR indisponível
            </div>
          )}
        </div>

        {/* Painel informativo — passo a passo + copia e cola */}
        <div className="space-y-5">
          <ol className="space-y-3.5 text-sm text-slate-700">
            {[
              'Abra o app do seu banco e acesse a área PIX.',
              'Escolha "Ler QR Code" ou cole o código abaixo.',
              'Confirme o valor e finalize. A confirmação é automática.',
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="leading-relaxed pt-0.5">{step}</span>
              </li>
            ))}
          </ol>

          {pix?.copy_paste && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Pix copia e cola
              </p>
              <button
                type="button"
                onClick={handleCopy}
                className="group w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white transition text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
              >
                <span className="text-xs font-mono text-slate-700 truncate">
                  {truncatedCopy}
                </span>
                <span
                  className={
                    'flex items-center gap-1.5 text-xs font-semibold flex-shrink-0 transition-colors ' +
                    (copied ? 'text-emerald-600' : 'text-slate-700 group-hover:text-slate-900')
                  }
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> Copiar
                    </>
                  )}
                </span>
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>
              Aguardando confirmação do pagamento
              {email ? (
                <>
                  {' '}— recibo será enviado para{' '}
                  <strong className="text-slate-700">{email}</strong>.
                </>
              ) : (
                '…'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Mini glifo PIX (losango) — evita dependência de ícone customizado externo. */
function PixGlyph({ className = '' }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className={className}>
      <path
        d="M12 2l4 4-4 4-4-4 4-4zm0 12l4 4-4 4-4-4 4-4zM2 12l4-4 4 4-4 4-4-4zm12 0l4-4 4 4-4 4-4-4z"
        fill="currentColor"
      />
    </svg>
  );
}
